/** Plain-word labels and pill tones for enum values. */
import type { TaskStage } from '@takeit4me/shared'

export type Tone = 'green' | 'amber' | 'red' | 'blue' | 'violet' | 'gray' | 'brand' | 'teal' | 'rose'

export const REASONS: Record<string, { label: string; tone: Tone }> = {
  asked_for_person: { label: 'Asked for a person', tone: 'red' },
  over_limit: { label: 'Above the AI limit', tone: 'amber' },
  risk_flag: { label: 'Risk flag', tone: 'red' },
  low_confidence: { label: 'Low confidence', tone: 'amber' },
  needs_consultant: { label: 'Consultant task', tone: 'teal' },
  clarification_stalled: { label: 'Clarifying stalled', tone: 'amber' },
  check_failed: { label: 'Check failed', tone: 'red' },
  autonomy_approval: { label: 'Approval needed', tone: 'amber' },
  shadow_review: { label: 'Shadow review', tone: 'gray' },
  knowledge_gap: { label: 'Knowledge gap', tone: 'blue' },
  identity: { label: 'Identity unclear', tone: 'gray' },
  vulnerability: { label: 'Vulnerability', tone: 'violet' },
  complaint: { label: 'Complaint', tone: 'red' },
  ai_unavailable: { label: 'The AI could not answer', tone: 'red' },
  stale_message: { label: 'An old message to check', tone: 'amber' },
  unmatched_sender: { label: 'Unknown sender', tone: 'amber' },
  send_link: { label: 'Send the link from a call', tone: 'blue' },
  promise_due: { label: 'A promise is due', tone: 'amber' },
  commitments_unchecked: { label: 'Check a reply for promises', tone: 'amber' },
  spam_suspect: { label: 'Possible spam', tone: 'amber' },
  unidentified_sender: { label: 'Who is the customer?', tone: 'amber' },
}

/** What a promise to a customer is about (promises.kind). */
export const PROMISE_KINDS: Record<string, string> = {
  person_reply: 'A person replies', callback: 'Call back', send_item: 'Send something', refund: 'Refund', follow_up: 'Follow up', other: 'Promise',
}
export const reason = (code: string) => REASONS[code] ?? { label: code.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()), tone: 'gray' as Tone }

export const STAGES: Record<TaskStage, { label: string; tone: Tone }> = {
  clarifying: { label: 'Asking customer', tone: 'amber' },
  assessing: { label: 'Assessing', tone: 'blue' },
  quoted: { label: 'Quote sent', tone: 'violet' },
  queued: { label: 'Queued', tone: 'gray' },
  running: { label: 'Agent working', tone: 'blue' },
  verifying: { label: 'Checking result', tone: 'blue' },
  awaiting_customer: { label: 'Preview sent', tone: 'violet' },
  with_consultant: { label: 'With consultant', tone: 'teal' },
  approved: { label: 'Approved', tone: 'green' },
  delivering: { label: 'Delivering', tone: 'blue' },
  done: { label: 'Done', tone: 'green' },
  cancelled: { label: 'Cancelled', tone: 'gray' },
  failed: { label: 'Failed', tone: 'red' },
}

/** The five task columns from the design: clarifying, running, waiting for customer, with consultant, done. */
export const STAGE_GROUPS: { key: string; label: string; stages: TaskStage[] }[] = [
  { key: 'attention', label: 'Needs attention', stages: ['failed'] },
  { key: 'clarifying', label: 'Clarifying', stages: ['clarifying'] },
  { key: 'running', label: 'Running', stages: ['assessing', 'queued', 'running', 'verifying', 'approved', 'delivering'] },
  { key: 'waiting', label: 'Waiting for customer', stages: ['awaiting_customer', 'quoted'] },
  { key: 'consultant', label: 'With a consultant', stages: ['with_consultant'] },
  { key: 'done', label: 'Done', stages: ['done', 'cancelled'] },
]

export const ROUTES: Record<string, { label: string; detail: string }> = {
  ai: { label: 'AI does it alone', detail: 'Small, clear, low risk. The result goes straight to the customer.' },
  ai_signoff: { label: 'AI does it, customer signs off', detail: 'The AI builds it and the customer approves a preview before it goes live.' },
  consultant: { label: 'A consultant does it', detail: 'Too broad or risky for the AI alone. A consultant leads, with AI help.' },
}

export const CONVERSATION_STATES: Record<string, { label: string; tone: Tone }> = {
  with_ai: { label: 'With the AI', tone: 'brand' },
  clarifying: { label: 'Clarifying', tone: 'amber' },
  waiting_customer: { label: 'Waiting for customer', tone: 'violet' },
  waiting_person: { label: 'Waiting for a person', tone: 'amber' },
  resolved: { label: 'Resolved', tone: 'green' },
  closed: { label: 'Closed', tone: 'gray' },
}

export const CHANNEL_NAMES: Record<string, string> = { email: 'Email', sms: 'SMS', chat: 'Web chat', voice: 'Phone', whatsapp: 'WhatsApp', task: 'Task', system: 'System' }

export const LEVEL_NAMES: Record<number, string> = { 0: 'Shadow', 1: 'Approve', 2: 'Veto window', 3: 'Autonomous' }
export const LEVEL_TONES: Record<number, string> = {
  0: 'bg-slate-100 text-slate-600 border-slate-200',
  1: 'bg-amber-50 text-amber-800 border-amber-200',
  2: 'bg-blue-50 text-blue-800 border-blue-200',
  3: 'bg-indigo-50 text-indigo-800 border-indigo-200',
}

export const CHANGE_STATUS: Record<string, { label: string; tone: Tone }> = {
  draft: { label: 'Draft · not live', tone: 'amber' },
  testing: { label: 'Testing', tone: 'blue' },
  tested: { label: 'Tested', tone: 'blue' },
  approved: { label: 'Approved', tone: 'violet' },
  released: { label: 'Live', tone: 'green' },
  rolled_back: { label: 'Rolled back', tone: 'gray' },
  rejected: { label: 'Rejected', tone: 'red' },
}

export const ENTITY_LABELS: Record<string, { label: string; tone: Tone }> = {
  decision_question: { label: 'Classification', tone: 'violet' },
  playbook: { label: 'Playbooks', tone: 'teal' },
  knowledge: { label: 'Knowledge', tone: 'blue' },
  task_template: { label: 'Tasks', tone: 'teal' },
  journey: { label: 'Journeys', tone: 'violet' },
  message_template: { label: 'Templates', tone: 'teal' },
  object_type: { label: 'CRM', tone: 'teal' },
  autonomy: { label: 'Autonomy', tone: 'brand' },
  company_settings: { label: 'Settings', tone: 'gray' },
  brand: { label: 'Brand', tone: 'rose' },
}

export const ROLE_LABELS: Record<string, string> = { admin: 'Admin', supervisor: 'Supervisor', specialist: 'Specialist', consultant: 'Consultant' }
